import { ImageIcon, Info, Loader2, Upload } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useHeroBanner } from "../hooks/use-hero-banner";

export function HeroBannerCard() {
  const { t } = useTranslation("settings");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { heroBannerUrl, heroBannerThumbnailUrl, hasBanner, loading, uploading, upload, remove } =
    useHeroBanner();

  const previewUrl = heroBannerThumbnailUrl ?? heroBannerUrl;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t("storefront.heroBanner.description")}</p>

      <div className="flex gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
        <div className="space-y-1">
          <p className="font-semibold">{t("storefront.heroBanner.uploadSpec.title")}</p>
          <ul className="list-disc space-y-0.5 pl-4 text-blue-800">
            <li>{t("storefront.heroBanner.uploadSpec.size")}</li>
            <li>{t("storefront.heroBanner.uploadSpec.safeArea")}</li>
            <li>{t("storefront.heroBanner.uploadSpec.format")}</li>
          </ul>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            upload(file);
          }
          event.target.value = "";
        }}
      />

      {loading ? (
        <Skeleton className="h-48 w-full" />
      ) : hasBanner && previewUrl ? (
        <div className="relative rounded-lg overflow-hidden border">
          <img
            src={previewUrl}
            alt={t("storefront.heroBanner.preview")}
            className="w-full h-48 object-cover"
          />
          {uploading && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <Loader2 className="h-8 w-8 text-white animate-spin" />
            </div>
          )}
          <div className="absolute top-2 right-2 flex gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <Upload className="mr-2 h-4 w-4" />
              {t("storefront.heroBanner.replaceButton")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={remove}
              disabled={uploading}
            >
              {t("storefront.heroBanner.removeButton")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-48 rounded-lg border-2 border-dashed border-muted-foreground/25 bg-muted/50">
          <div className="text-center space-y-2">
            <ImageIcon className="h-10 w-10 mx-auto text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              {t("storefront.heroBanner.placeholder")}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("storefront.heroBanner.uploading")}
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  {t("storefront.heroBanner.uploadButton")}
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
